import { useState } from "react";
import { useLoaderData, useRevalidator, type LoaderFunctionArgs } from "react-router";
import { Loading, SettingsRowsSkeleton } from "../../components/skeletons.jsx";
import { Deferred, EmptyState } from "../../components/states.jsx";
import { useI18n } from "../../i18n/index.js";
import { api } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { apiContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { Button, Callout, ConfirmDialog, CopyIcon, Dialog, FormError, KeyIcon, PageHeader, PlusIcon, SettingsBlock, SettingsRow, SettingsSection, TextField, Tooltip, useToast, Skeleton } from "../../ui/index.js";

export function apiKeysLoader({ context }: LoaderFunctionArgs) {
  const ws = context.get(workspaceContext);
  const keys = context.get(apiContext).workspace(ws.id).listApiKeys().then((page) => page.items);
  return { workspaceId: ws.id, keys: deferred(keys) };
}

function CreateKeyDialog({ workspaceId, onCreated }: { workspaceId: string; onCreated: (secret: string) => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        setOpen(next);
        if (!next) setError(null);
      }}
    >
      <Dialog.Trigger render={<Button variant="primary" icon={<PlusIcon />} />}>{t("apiKeys.create")}</Dialog.Trigger>
      <Dialog.Content title={t("apiKeys.create.title")} description={t("apiKeys.create.description")}>
        <form
          className="dt-form"
          onSubmit={async (e) => {
            e.preventDefault();
            const name = String(new FormData(e.currentTarget).get("name"));
            setBusy(true);
            setError(null);
            try {
              const created = await api.workspace(workspaceId).createApiKey({ name });
              setOpen(false);
              onCreated(created.key);
            } catch (err) {
              setError(err instanceof Error ? err.message : String(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          <TextField name="name" label={t("apiKeys.create.name")} placeholder={t("apiKeys.create.namePlaceholder")} required autoFocus />
          <FormError message={error} />
          <div className="dt-form__actions" style={{ justifyContent: "flex-end" }}>
            <Dialog.Close render={<Button variant="ghost" />} disabled={busy}>
              {t("common.cancel")}
            </Dialog.Close>
            <Button type="submit" variant="primary" loading={busy}>
              {t("apiKeys.create")}
            </Button>
          </div>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export function ApiKeysSkeleton() {
  const { t } = useI18n();
  return (
    <Loading>
      <PageHeader title={t("apiKeys.title")} description={<Skeleton width={120} />} actions={<Skeleton shape="button" width={112} />} />
      <SettingsSection>
        <SettingsRowsSkeleton count={3} control={<Skeleton shape="button-sm" />} />
      </SettingsSection>
    </Loading>
  );
}

export function ApiKeysRoute() {
  const { workspaceId, keys } = useLoaderData<typeof apiKeysLoader>();
  const revalidator = useRevalidator();
  const { t, tPlural, formatDate } = useI18n();
  const toast = useToast();
  const [minted, setMinted] = useState<string | null>(null);

  const copy = async () => {
    if (!minted) return;
    try {
      await navigator.clipboard.writeText(minted);
      toast.success(t("apiKeys.copied"));
    } catch {
      toast.danger(t("apiKeys.clipboardUnavailable"), t("apiKeys.clipboardHint"));
    }
  };

  // The description counts keys, so the header waits with the list.
  return (
    <Deferred data={keys} fallback={<ApiKeysSkeleton />}>
      {(keys) => (
        <>
          <PageHeader
            title={t("apiKeys.title")}
            description={keys.length === 0 ? t("apiKeys.noneCreated") : tPlural("apiKeys.count", keys.length)}
            actions={
              <CreateKeyDialog
                workspaceId={workspaceId}
                onCreated={(secret) => {
                  setMinted(secret);
                  revalidator.revalidate();
                }}
              />
            }
          />
          {minted && (
            <Callout tone="warning" title={t("apiKeys.minted.title")}>
              <div className="dt-secret ph-no-capture" style={{ marginTop: "var(--dt-space-2)" }}>
                <code data-testid="minted-key">{minted}</code>
                <Tooltip content={t("apiKeys.copyTooltip")}>
                  <Button variant="secondary" size="sm" icon={<CopyIcon />} onClick={copy} aria-label={t("apiKeys.copyAria")}>
                    {t("common.copy")}
                  </Button>
                </Tooltip>
              </div>
            </Callout>
          )}
          <SettingsSection>
            {keys.length === 0 ? (
              <SettingsBlock>
                <EmptyState title={t("apiKeys.empty.title")} hint={t("apiKeys.empty.hint")} icon={<KeyIcon size={18} />} />
              </SettingsBlock>
            ) : (
              keys.map((k) => (
                <SettingsRow
                  key={k.id}
                  label={k.name ?? t("common.none")}
                  description={
                    <>
                      <code>{k.start}…</code> · {t("apiKeys.created", { date: formatDate(k.createdAt) })}
                    </>
                  }
                >
                  <ConfirmDialog
                    trigger={
                      <Button variant="ghost" size="sm">
                        {t("apiKeys.revoke")}
                      </Button>
                    }
                    title={t("apiKeys.revoke.title", { name: k.name ?? "" })}
                    description={t("apiKeys.revoke.description")}
                    confirmLabel={t("apiKeys.revoke.confirm")}
                    destructive
                    onConfirm={async () => {
                      await api.workspace(workspaceId).deleteApiKey(k.id);
                      toast.neutral(t("apiKeys.revoked"), k.name);
                      revalidator.revalidate();
                    }}
                  />
                </SettingsRow>
              ))
            )}
          </SettingsSection>
        </>
      )}
    </Deferred>
  );
}
